import type { FormEvent } from 'react';
import React, { useCallback, useState } from 'react';

import { FormControl, InputLabel } from '@this/src/components/shared/ui/inputs/form_control';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Textarea } from '@this/shared/ui/inputs/textarea/textarea';
import { useAITTheme } from '@this/shared/ui/theme';
import { getSpacingFromToken } from '@this/shared/ui/theme/tokens/token.utils';
import Notification from '../../../../notification';

type MemoFormProps = {
  inputName: string;
  label: string;
  initialValue: string;
  placeholder?: string;
  onSubmit: (memo: string) => Promise<void>;
  testId?: string;
};

export const MemoForm = ({ inputName, label, initialValue, placeholder, onSubmit, testId }: MemoFormProps) => {
  const { theme } = useAITTheme();
  const [memo, setMemo] = useState<string>(initialValue);
  const [backupMemo, setBackupMemo] = useState<string>(initialValue);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setSubmitting(true);
      try {
        await onSubmit(memo);
        setBackupMemo(memo);
        Notification.success(`${label}を保存しました。`);
      } catch {
        Notification.error('通信環境が不安定です。画面をリロードしてからもう一度お試しください。');
      } finally {
        setSubmitting(false);
      }
    },
    [onSubmit, memo, label]
  );

  const handleMemoChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setMemo(value);
  }, []);

  const handleUndoClick = useCallback(() => {
    setMemo(backupMemo);
  }, [backupMemo]);

  return (
    <form onSubmit={handleSubmit} data-test={`virtual-counter-memo-${inputName}`} data-testid={testId}>
      <FormControl fullWidth>
        <InputLabel>{label}</InputLabel>
        <Textarea
          name={inputName}
          value={memo}
          placeholder={placeholder}
          onChange={handleMemoChange}
          data-test="virtual-counter-memo-textaea"
          rowsMin={1}
          rowsMax={1}
          style={{
            border: `2px solid ${theme.tokens.colors.brand.primary}`,
            margin: 0
          }}
        />
      </FormControl>

      <div
        style={{
          marginTop: getSpacingFromToken(theme.tokens, 2),
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '4px'
        }}
      >
        <Button
          size="small"
          color="sub"
          loading={submitting}
          onClick={handleUndoClick}
          data-test="virtual-counter-memo-undo-button"
        >
          編集前に戻す
        </Button>
        <Button type="submit" size="small" loading={submitting} data-test="virtual-counter-memo-submit-button">
          保存
        </Button>
      </div>
    </form>
  );
};
